import React from "react";
import Button from "components/common/ui/buttons/Button/Button";
import styled from "styled-components";
import Modal from "../create/Modal";
import Dropdown from "../shared/Dropdown";
import Input from "../shared/Input";
import { FeatureRequestListItem } from "interfaces/feature-request";
import { DropdownOption, useDropdownOptions } from "hooks/useDropdownValues";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";
import { featureFlags } from "services/common/config/featureFlags";
import ButtonFilter, { StyledButton } from "../shared/ButtonFilter";

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const Header = styled.h4`
    margin: 0;
    font-weight: 600;
`;

const FilterContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 1rem;
    width: 100%;
    gap: 1rem;

    & > *:first-child {
        flex: 1;
    }

    & > *:last-child {
        flex-shrink: 0;
    }
`;

const AdditionalFilterContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    margin-bottom: 1rem;
    width: 100%;
    gap: 1rem;

    & > * {
        flex: 1;
    }
`;

const getDropdownOptions = (
    req: FeatureRequestListItem,
    field: keyof FeatureRequestListItem
): string[] =>
    req[field]
        .split(", ")
        .map((m) => m.trim())
        .filter((m) => m.length > 0);

const priorityComparator = (a: string, b: string): number => {
    const order: { [key: string]: number } = { 1: 0, 2: 1, 3: 2, 0: 3 };

    return (order[a] ?? 999) - (order[b] ?? 999);
};

const getStateOptions = (featureRequests: FeatureRequestListItem[]): DropdownOption[] =>
    [
        {
            value: "",
            label: "Kaikki",
        },
        {
            value: "Uusi",
            label: "Uudet",
        },
        {
            value: "Käsittelyssä",
            label: "Tulossa käsittelyyn",
        },
        {
            value: "Suositeltu",
            label: "Suositeltu",
        },
        {
            value: "Ratkaistu",
            label: "Ratkaistu",
        },
        {
            value: "Hylätty",
            label: "Hylätty",
        },
    ].map((option) => ({
        ...option,
        label: `${option.label} (${
            featureRequests.filter((req) => req.state === option.value || option.value === "")
                .length
        })`,
    }));

const TableFilters: React.FC<{
    getFilterValue: (filterName: string) => string;
    setFilterValue: (filterName: string, value: string) => void;
    resetFilters: () => void;
    featureRequests: FeatureRequestListItem[];
}> = ({ getFilterValue, setFilterValue, resetFilters, featureRequests }) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const translate = useTranslate();

    const priorityOptions = useDropdownOptions(
        featureRequests,
        (req) => getDropdownOptions(req, "datahubPriority"),
        (value) => translate(`featureRequests.priority.${value}`),
        priorityComparator
    );
    const businessAreaOptions = useDropdownOptions(featureRequests, (req) =>
        getDropdownOptions(req, "businessArea")
    );
    const versionOptions = useDropdownOptions(featureRequests, (req) =>
        getDropdownOptions(req, "dhVersion")
    );
    const meetingOptions = useDropdownOptions(featureRequests, (req) =>
        getDropdownOptions(req, "nextOrLastMeetingDate")
    );

    return (
        <div>
            <HeaderContainer>
                <Header>
                    {translate("featureRequests.featureRequestsControls.reviewFeatureRequests")}
                </Header>
                {featureFlags.newFeatureRequest && (
                    <Button onClick={() => setIsModalOpen(true)}>
                        {translate("featureRequests.featureRequestsControls.newFeatureRequest")}
                    </Button>
                )}
            </HeaderContainer>
            <FilterContainer>
                <Input
                    label={translate("featureRequests.featureRequestsControls.search")}
                    placeholder={translate(
                        "featureRequests.featureRequestsControls.searchPlaceholder"
                    )}
                    value={getFilterValue("search")}
                    onChange={(value) => setFilterValue("search", value)}
                />
                <ButtonFilter
                    label={translate("featureRequests.featureRequestsControls.state")}
                    options={getStateOptions(featureRequests)}
                    selectedValue={getFilterValue("state")}
                    onValueChange={(value) => setFilterValue("state", value)}
                />
            </FilterContainer>
            <AdditionalFilterContainer>
                <Dropdown
                    label={translate("featureRequests.featureRequestsControls.meeting")}
                    options={meetingOptions}
                    selectedValue={getFilterValue("meeting")}
                    onValueChange={(value) => setFilterValue("meeting", value)}
                    placeholder={translate("featureRequests.featureRequestsControls.allMeetings")}
                    fullWidth={true}
                />
                <Dropdown
                    label={translate("featureRequests.featureRequestsControls.priority")}
                    options={priorityOptions}
                    selectedValue={getFilterValue("priority")}
                    onValueChange={(value) => setFilterValue("priority", value)}
                    placeholder={translate("featureRequests.featureRequestsControls.allPriorities")}
                    fullWidth={true}
                />
                <Dropdown
                    label={translate("featureRequests.featureRequestsControls.businessArea")}
                    options={businessAreaOptions}
                    selectedValue={getFilterValue("businessArea")}
                    onValueChange={(value) => setFilterValue("businessArea", value)}
                    placeholder={translate("featureRequests.featureRequestsControls.allAreas")}
                    fullWidth={true}
                />
                <Dropdown
                    label={translate("featureRequests.featureRequestsControls.version")}
                    options={versionOptions}
                    selectedValue={getFilterValue("version")}
                    onValueChange={(value) => setFilterValue("version", value)}
                    placeholder={translate("featureRequests.featureRequestsControls.allVersions")}
                    fullWidth={true}
                />
                <StyledButton isActive={false} onClick={resetFilters}>
                    {translate("featureRequests.buttons.resetFilters")}
                </StyledButton>
            </AdditionalFilterContainer>
            <Modal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} />
        </div>
    );
};

export default TableFilters;
